import { PomodoroStatsData, DailyPomodoroStats } from '../types';

const STORAGE_KEY = 'pomodoro-stats';

export class PomodoroStatsService {
  static getStats(): PomodoroStatsData {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return this.getDefaultStats();
      }
      return JSON.parse(stored);
    } catch (error) {
      console.error('Error reading Pomodoro stats from localStorage:', error);
      return this.getDefaultStats();
    }
  }

  static saveStats(stats: PomodoroStatsData): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stats));
    } catch (error) {
      console.error('Error saving Pomodoro stats to localStorage:', error);
    }
  }

  static addCompletedPomodoro(): PomodoroStatsData {
    const stats = this.getStats();
    const today = this.getTodayString();
    
    // Find or create today's stats
    let todayStats = stats.dailyStats.find(day => day.date === today);
    if (!todayStats) {
      todayStats = {
        date: today,
        completedPomodoros: 0,
        fullCycles: 0
      };
      stats.dailyStats.push(todayStats);
    }

    // Increment completed pomodoros
    todayStats.completedPomodoros += 1;
    stats.totalPomodoros += 1;

    // Check if this completes a full cycle (4 pomodoros)
    if (todayStats.completedPomodoros % 4 === 0) {
      todayStats.fullCycles += 1;
      stats.totalCycles += 1;
    }

    // Recalculate streaks
    this.calculateStreaks(stats);

    // Sort daily stats by date (newest first) and keep only last 365 days
    stats.dailyStats.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    stats.dailyStats = stats.dailyStats.slice(0, 365);

    this.saveStats(stats);
    return stats;
  }

  static getTodayStats(): DailyPomodoroStats {
    const stats = this.getStats();
    const today = this.getTodayString();
    
    return stats.dailyStats.find(day => day.date === today) || {
      date: today,
      completedPomodoros: 0,
      fullCycles: 0
    };
  }

  private static getDefaultStats(): PomodoroStatsData {
    return {
      dailyStats: [],
      totalPomodoros: 0,
      totalCycles: 0,
      currentStreak: 0,
      bestStreak: 0
    };
  }

  private static getTodayString(): string {
    return new Date().toISOString().split('T')[0];
  }

  private static calculateStreaks(stats: PomodoroStatsData): void {
    if (stats.dailyStats.length === 0) {
      stats.currentStreak = 0;
      stats.bestStreak = 0;
      return;
    }

    // Sort by date ascending for streak calculation
    const sortedStats = [...stats.dailyStats].sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    let currentStreak = 0;
    let maxStreak = 0;
    let tempStreak = 0;

    const today = this.getTodayString();
    const todayDate = new Date(today);

    // Calculate current streak (working backwards from today)
    let checkDate = new Date(today);
    let foundToday = false;

    for (let i = 0; i < 365; i++) {
      const checkDateString = checkDate.toISOString().split('T')[0];
      const dayStats = sortedStats.find(day => day.date === checkDateString);
      
      if (dayStats && dayStats.completedPomodoros > 0) {
        if (!foundToday) {
          foundToday = true;
        }
        currentStreak++;
      } else {
        // If we haven't found today yet, keep looking
        if (!foundToday && checkDateString !== today) {
          // Continue checking previous days
        } else {
          // Break the streak
          break;
        }
      }
      
      checkDate.setDate(checkDate.getDate() - 1);
    }

    // Calculate best streak
    for (const dayStats of sortedStats) {
      if (dayStats.completedPomodoros > 0) {
        tempStreak++;
        maxStreak = Math.max(maxStreak, tempStreak);
      } else {
        tempStreak = 0;
      }
    }

    stats.currentStreak = currentStreak;
    stats.bestStreak = Math.max(maxStreak, currentStreak);
  }
} 